import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import type { AgentSessionFileDto } from '../../types/file';
import { fileService } from '../../services/fileService';
import { useNotificationStore } from '../../store/notificationStore';

export interface FilePreviewProps {
  open: boolean;
  file: AgentSessionFileDto | null;
  onClose: () => void;
}

/**
 * Modal preview for a file (images, PDFs, and text).
 */
export const FilePreview = ({ open, file, onClose }: FilePreviewProps) => {
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);

  const contentType = file?.contentType || 'application/octet-stream';

  const kind = useMemo(() => {
    if (!file) return 'unknown';
    if (contentType.startsWith('image/')) return 'image';
    if (contentType === 'application/pdf') return 'pdf';
    if (contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('xml'))
      return 'text';
    return 'unknown';
  }, [file, contentType]);

  useEffect(() => {
    if (!open || !file) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setText(null);

      try {
        const resp = await fileService.downloadFileBlob(file.id);
        if (cancelled) return;

        const url = URL.createObjectURL(resp.data);
        setBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });

        if (kind === 'text') {
          const t = await resp.data.text();
          if (!cancelled) setText(t);
        }
      } catch (err: any) {
        const msg = err.response?.data?.message || err.message || 'Failed to load file preview';
        addNotification(msg, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, file?.id, kind]);

  const handleDownload = () => {
    if (!blobUrl || !file) return;
    try {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = file.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      addNotification(e?.message || 'Failed to download file', 'error');
    }
  };

  const handleClose = () => {
    setText(null);
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{file?.fileName || 'File preview'}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !file ? (
          <Typography variant="body2" color="text.secondary">
            No file selected.
          </Typography>
        ) : !blobUrl ? (
          <Typography variant="body2" color="text.secondary">
            Preview unavailable.
          </Typography>
        ) : kind === 'image' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={blobUrl}
              alt={file.fileName}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        ) : kind === 'pdf' ? (
          <iframe
            title={file.fileName}
            src={blobUrl}
            style={{ width: '100%', height: '70vh', border: 0 }}
          />
        ) : kind === 'text' ? (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1,
              bgcolor: 'grey.100',
              overflow: 'auto',
              maxHeight: '70vh',
              fontSize: 12,
            }}
          >
            {text ?? '(loading text...)'}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No inline preview available for content type: {contentType}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!blobUrl || !file}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

